import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import frames from '../data/asciiFrames';
import '../styles/AsciiAnimation.css';

const AsciiAnimation = () => {
  const [currentFrame, setCurrentFrame] = useState(0);
  const frameRef = useRef(0);
  const lastTimeRef = useRef(0);
  const rafIdRef = useRef(null);
  const isVisibleRef = useRef(true);
  const containerRef = useRef(null);
  
  const fps = 12; // Reduced from 15 for better performance
  const frameInterval = 1000 / fps;

  // Memoize processed frames
  const processedFrames = useMemo(() => {
    return frames || [];
  }, []);

  // Use requestAnimationFrame for smoother, more efficient animation
  const animate = useCallback((timestamp) => {
    if (!isVisibleRef.current) {
      rafIdRef.current = requestAnimationFrame(animate);
      return;
    }

    if (!lastTimeRef.current) {
      lastTimeRef.current = timestamp;
    }

    const elapsed = timestamp - lastTimeRef.current;

    if (elapsed >= frameInterval) {
      frameRef.current = (frameRef.current + 1) % processedFrames.length;
      setCurrentFrame(frameRef.current);
      lastTimeRef.current = timestamp - (elapsed % frameInterval);
    }

    rafIdRef.current = requestAnimationFrame(animate);
  }, [frameInterval, processedFrames.length]);

  useEffect(() => {
    if (processedFrames.length === 0) {
      return;
    }

    // Start animation
    rafIdRef.current = requestAnimationFrame(animate);

    // Pause animation when tab is not visible (saves CPU)
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
      if (!document.hidden) {
        lastTimeRef.current = 0; // Reset timing when becoming visible
      }
    };

    // Use Intersection Observer to pause when not in viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isVisibleRef.current = entry.isIntersecting && !document.hidden;
        });
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      observer.disconnect();
    };
  }, [processedFrames.length, animate]);

  if (!processedFrames.length) {
    return null;
  }

  return (
    <div className="ascii-animation-container" ref={containerRef}>
      <div className="ascii-animation-overlay" />
      <pre className="ascii-art">
        {processedFrames[currentFrame]}
      </pre>
    </div>
  );
};

export default AsciiAnimation;
